// Die Karten des Dashboards — zeichnen, was dashboard-model.ts ausrechnet.

import type { AttentionItem, Dashboard } from "../../api/types";
import { fmtAgo, fmtInt, fmtUsd, levelTone, pct } from "../../lib/format";
import { PLATFORM_EVENT, RUN_KIND, runStateOf, stateOf } from "../../lib/labels";
import { Link, useNavigate } from "../../router/router";
import { Badge, Meter } from "../../ui/badge";
import { Card } from "../../ui/card";
import {
  SEVERITY, hostGauges, instanceBars, kpiTiles, sortTenantsByCost, splitAttention, tenantNames, tenantTotal, type KpiTile,
} from "./dashboard-model";

function Kpi({ tile }: { tile: KpiTile }) {
  const inner = (
    <>
      <span className="pf-kpi__label">{tile.label}</span>
      <span className="pf-kpi__value">{tile.value}</span>
      {tile.sub ? <span className="pf-kpi__sub">{tile.sub}</span> : null}
      {tile.bar ? <Meter pct={tile.bar.pct} tone={tile.bar.tone} /> : null}
    </>
  );
  return tile.href ? <Link className="pf-kpi pf-kpi--link" to={tile.href}>{inner}</Link> : <div className="pf-kpi">{inner}</div>;
}

export function Kpis({ data, hostPending }: { data: Dashboard; hostPending: boolean }) {
  return <div className="pf-kpis">{kpiTiles(data, hostPending).map((t) => <Kpi key={t.label} tile={t} />)}</div>;
}

export function AttentionCard({ data }: { data: Dashboard }) {
  const { shown, more, summary } = splitAttention(data.attention);
  const names = tenantNames(data);
  const row = (i: AttentionItem, n: number) => {
    const [label, tone] = SEVERITY[i.severity] ?? [i.severity, ""];
    return (
      <li key={`${i.code}-${i.tenant ?? "host"}-${n}`}>
        <Link className={`pf-attn__item pf-attn__item--${tone}`} to={i.href}>
          <span className="pf-attn__sev">{label}</span>
          <span className="pf-attn__text"><strong>{i.tenant ? names.get(i.tenant) ?? i.tenant : "Host"}</strong> — {i.text}</span>
          <span className="pf-attn__go" aria-hidden="true">›</span>
        </Link>
      </li>
    );
  };
  const items = data.attention;
  return (
    <Card title="Braucht Aufmerksamkeit" hint={summary} flush={items.length > 0}>
      {items.length ? (
        <>
          <ul className="pf-attn">{shown.map(row)}</ul>
          {more.length ? (
            <details className="pf-attn__more">
              <summary>{more.length} weitere Hinweise</summary>
              <ul className="pf-attn">{more.map(row)}</ul>
            </details>
          ) : null}
        </>
      ) : (
        <div className="pf-calm"><span className="pf-calm__dot" aria-hidden="true" />Alles in Ordnung — kein Mandant und kein Host braucht gerade etwas.</div>
      )}
    </Card>
  );
}

export function TenantsCard({ data }: { data: Dashboard }) {
  const navigate = useNavigate();
  const rows = sortTenantsByCost(data.tenants);
  if (!rows.length) {
    return <Card title="Mandanten"><div className="pf-empty">Noch kein Mandant. <Link to="/tenants/new">Neuen Mandanten anlegen</Link></div></Card>;
  }
  return (
    <Card title="Mandanten" hint="Nach Kosten im Monat" flush actions={<Link className="pf-btn pf-btn--sm pf-btn--ghost" to="/tenants">Alle</Link>}>
      <div className="pf-table-wrap">
        <table className="pf-table pf-table--compact">
          <thead><tr><th>Mandant</th><th className="pf-num">Benutzer</th><th>Budget</th><th className="pf-num">Kosten</th></tr></thead>
          <tbody>
            {rows.map((t) => {
              const [st, tn] = stateOf(t.state);
              const approved = t.status?.users?.approved;
              const max = t.license?.max_users;
              const spend = t.cost?.platform_key_spend_usd;
              const budget = t.cost?.platform_key_budget_usd;
              const bp = budget ? pct(spend ?? 0, budget) : null;
              const href = `/tenants/${t.id}`;
              return (
                <tr key={t.id} data-href={href} onClick={() => navigate(href)}>
                  <td><span className="pf-cell-name">{t.name || t.slug}</span> <Badge tone={tn}>{st}</Badge><div className="pf-sub">{t.host}{t.version ? ` · ${t.version}` : ""}</div></td>
                  <td className="pf-num">{approved === undefined ? "—" : <>{fmtInt(approved)}{max ? <span className="pf-sub"> / {fmtInt(max)}</span> : null}</>}</td>
                  <td className="pf-budget">{bp === null ? <span className="pf-sub">—</span> : <><Meter pct={bp} tone={levelTone(bp)} /><span className="pf-sub">{Math.round(bp)} % von {fmtUsd(budget)}</span></>}</td>
                  <td className="pf-num">{t.cost ? fmtUsd(tenantTotal(t)) : "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

export function HostCard({ data, pending }: { data: Dashboard; pending: boolean }) {
  if (pending) return <Card title="Host"><div className="pf-empty">Wird gemessen …</div></Card>;
  const h = data.host;
  if (!h) return <Card title="Host"><div className="pf-note pf-note--warn">Der Provisioner antwortet nicht — keine Werte vom Host.</div></Card>;
  const bars = instanceBars(h, tenantNames(data));
  return (
    <Card title="Host" hint={`${h.cpus} CPUs · ${(h.memory_mb / 1024).toFixed(1)} GB · ${fmtInt(h.containers_running)} Container`}>
      <div className="pf-gauges">
        {hostGauges(h).map((g) => (
          <div key={g.label} className="pf-gauge">
            <div className="pf-gauge__top"><span>{g.label}</span><span className="pf-sub">{g.value}</span></div>
            <Meter pct={g.pct} tone={g.tone} />
          </div>
        ))}
      </div>
      {bars.length ? (
        <>
          <div className="pf-subhead">Speicher je Mandant</div>
          <ul className="pf-bars">
            {bars.map((b) => (
              <li key={b.key} className="pf-bars__row">
                <span className="pf-bars__name">{b.name}</span>
                <span className="pf-bars__bar"><span style={{ width: `${b.pct.toFixed(1)}%` }} /></span>
                <span className="pf-bars__val">{b.value}<span className="pf-sub"> · {b.containers}</span></span>
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </Card>
  );
}

export function RunsCard({ data }: { data: Dashboard }) {
  if (!data.runs.length) return <Card title="Letzte Läufe"><div className="pf-empty">Noch keine Läufe.</div></Card>;
  return (
    <Card title="Letzte Läufe" flush>
      <ul className="pf-feed">
        {data.runs.map((r) => {
          const [st, tn] = runStateOf(r.state);
          return (
            <li key={r.id}>
              <Link className="pf-feed__item" to={`/tenants/${r.tenant_id}`}>
                <span className="pf-feed__main">
                  <strong>{RUN_KIND[r.kind] ?? r.kind}</strong><span className="pf-sub">{r.tenant_name || r.tenant_slug || "?"}</span>
                  {r.state === "failed" && r.error ? <span className="pf-feed__error">{r.error}</span> : null}
                </span>
                <span className="pf-feed__meta"><Badge tone={tn}>{st}</Badge><span className="pf-sub">{fmtAgo(r.finished_at ?? r.created_at)}</span></span>
              </Link>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

export function ActivityCard({ data }: { data: Dashboard }) {
  if (!data.audit.length) return <Card title="Letzte Aktivität"><div className="pf-empty">Noch keine Einträge.</div></Card>;
  return (
    <Card title="Letzte Aktivität" flush actions={<Link className="pf-btn pf-btn--sm pf-btn--ghost" to="/audit">Alle</Link>}>
      <ul className="pf-feed">
        {data.audit.map((e) => (
          <li key={e.id}>
            <div className="pf-feed__item">
              <span className="pf-feed__main">
                <strong>{PLATFORM_EVENT[e.event] ?? e.event}</strong>
                <span className="pf-sub">{e.actor_name || e.actor_email}{e.tenant_slug ? ` · ${e.tenant_slug}` : ""}</span>
              </span>
              <span className="pf-feed__meta"><span className="pf-sub">{fmtAgo(e.created_at)}</span></span>
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}
